/**
 * ProjectLinkedResourcesPane — what is attached to this project, by type.
 *
 * The same breakdown the Details tab's right column carried, minus its card
 * chrome: the pane already has a header and a close button, so a `GlassCard`
 * inside one reads as a card in a card.
 *
 * It counts; the Resources TAB lists. Pressing a row goes to the tab rather
 * than duplicating the table in a 320px dock.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiBookOpenTextLight,
  PiBriefcaseLight,
  PiCheckSquareOffsetLight,
  PiDatabaseLight,
  PiFileLight,
  PiFileMagnifyingGlassLight,
  PiFileTextLight,
  PiLockKeyLight,
  PiPlusLight,
  PiTableLight,
} from 'react-icons/pi';
import Button from '../../shared/TailwindComponents/Button';
import PaneFooter from '../../shared/surface/PaneFooter';

const TYPE_ICONS: Record<string, React.ReactElement> = {
  matter: <PiBriefcaseLight className="w-4 h-4 text-info-500" />,
  task: <PiCheckSquareOffsetLight className="w-4 h-4 text-success-500" />,
  table: <PiTableLight className="w-4 h-4 text-teal-500" />,
  review: <PiBookOpenTextLight className="w-4 h-4 text-success-500" />,
  playbook: <PiBookOpenTextLight className="w-4 h-4 text-success-500" />,
  review_session: <PiFileMagnifyingGlassLight className="w-4 h-4 text-sky-500" />,
  datasource: <PiDatabaseLight className="w-4 h-4 text-cyan-500" />,
  template: <PiFileTextLight className="w-4 h-4 text-slate-500" />,
};

const TYPE_LABELS: Record<string, string> = {
  matter: 'Matters',
  task: 'Tasks',
  table: 'Tables',
  review: 'Playbooks',
  playbook: 'Playbooks',
  review_session: 'Reviews',
  datasource: 'Data Sources',
  template: 'Templates',
};

interface Props {
  project: any;
  /** The resources slice, for the restricted tally the record does not carry. */
  resources: any;
  canEdit: boolean;
  /** Opens the "link resources" side panel. */
  onLink: () => void;
  /** Switches the canvas to the Resources tab. */
  onOpenResources: () => void;
}

const ProjectLinkedResourcesPane: React.FC<Props> = ({
  project,
  resources,
  canEdit,
  onLink,
  onOpenResources,
}) => {
  const { t } = useTranslation();

  const counts: Array<[string, number]> = Object.entries(
    project?.resource_counts || {}
  ) as Array<[string, number]>;
  const restricted = resources?.restricted_count ?? 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-2">
        {project?.total_resources > 0 ? (
          <>
            {counts.map(([type, count]) => (
              <button
                key={type}
                type="button"
                onClick={onOpenResources}
                className="w-full flex items-center justify-between py-1.5 px-1 rounded hover:bg-surface-soft transition-colors"
              >
                <span className="flex items-center gap-2">
                  {TYPE_ICONS[type] || <PiFileLight className="w-4 h-4 text-fg-muted" />}
                  <span className="text-sm text-fg-body">{t(TYPE_LABELS[type] || type)}</span>
                </span>
                <span className="text-sm font-medium text-fg-strong">{count}</span>
              </button>
            ))}
            {/* No "Total" row: the footer below carries the tally now. Only
                the restricted count — a fact the footer does not state —
                stays in the body. */}
            {restricted > 0 && (
              <div className="pt-2 mt-2 border-t border-hairline-soft flex items-center gap-0.5 px-1 text-[10px] text-fg-muted">
                <PiLockKeyLight className="w-3 h-3" />
                {restricted} {t('restricted')}
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-fg-muted text-center py-4">
            {t('No resources linked yet.')}
          </p>
        )}
      </div>

      {/* Stat left, action right — the pane footer every fixed-height pane
          carries. See `shared/surface/PaneFooter`. */}
      <PaneFooter
        count={project?.total_resources || 0}
        label={(project?.total_resources || 0) === 1 ? t('resource') : t('resources')}
      >
        {canEdit && (
          <Button variant="outline" size="small" onClick={onLink}>
            <PiPlusLight className="h-3.5 w-3.5 mr-1" />
            {t('Link resource')}
          </Button>
        )}
      </PaneFooter>
    </div>
  );
};

export default ProjectLinkedResourcesPane;
